import React, { useEffect, useMemo, useRef, useState, useId } from 'react';
import { Box, TextField, Typography, IconButton, InputAdornment, Popover, Grid, Button, FormHelperText } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import {alpha, useTheme} from "@mui/material/styles";
import {
  DATE_FORMAT_HINT,
  MONTH_YEAR_FORMAT_HINT,
  normalizeToDdMmYyyy,
  parseMMYYYY,
  formatMMYYYY,
  formatMonthYearInput,
  formatDateInput,
  parseDDMMYYYY,
} from '@/utils/dataUtils';

export interface RangeDatePickerProps {
  label?: string;
  from: string | null; // DD/MM/YYYY, or MM/YYYY when precision=month
  to: string | null;   // DD/MM/YYYY, or MM/YYYY when precision=month
  onChangeFrom: (v: string | null) => void;
  onChangeTo: (v: string | null) => void;
  minDate?: string | null; // DD/MM/YYYY
  maxDate?: string | null; // DD/MM/YYYY
  disabled?: boolean;
  /** When true, From is read-only (display); To remains editable unless disabled. */
  displayFrom?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  variant?: 'outlined' | 'filled' | 'standard';
  compact?: boolean; // mimic InputField compact style
  error?: boolean; // Show error state on both fields
  errorFrom?: boolean; // Show error state on from field
  errorTo?: boolean; // Show error state on to field
  /**
   * day = DD/MM/YYYY (default). month = MM/YYYY for from/to and month grid.
   * minDate/maxDate stay DD/MM/YYYY and are compared by calendar month.
   */
  precision?: 'day' | 'month';
}

function parseDMY(value?: string | null): Date | null {
  if (!value) return null;
  const m = /^([0-9]{2})\/([0-9]{2})\/([0-9]{4})$/.exec(value);
  if (!m) return null;
  const d = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const y = Number(m[3]);
  const dt = new Date(y, mo, d);
  if (dt.getFullYear() === y && dt.getMonth() === mo && dt.getDate() === d) return dt;
  return null;
}
function toDMY(date: Date): string {
  const d = `${date.getDate()}`.padStart(2, '0');
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const y = `${date.getFullYear()}`;
  return `${d}/${m}/${y}`;
}

const sod = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const isBefore = (a: Date, b: Date) => sod(a).getTime() < sod(b).getTime();
const isAfter = (a: Date, b: Date) => sod(a).getTime() > sod(b).getTime();

const buildMonthMatrix = (year: number, month: number) => {
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);
  const startWeekday = start.getDay();
  const daysInMonth = end.getDate();
  const weeks: Array<Array<Date | null>> = [];
  let current: Array<Date | null> = [];
  for (let i = 0; i < startWeekday; i++) current.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    current.push(new Date(year, month, day));
    if (current.length === 7) {
      weeks.push(current);
      current = [];
    }
  }
  if (current.length) {
    while (current.length < 7) current.push(null);
    weeks.push(current);
  }
  return weeks;
};

const MonthGrid: React.FC<{
  year: number;
  month: number; // 0..11
  selectedStart: Date | null;
  selectedEnd: Date | null;
  min?: Date | null;
  max?: Date | null;
  onSelect: (d: Date) => void;
}> = ({ year, month, selectedStart, selectedEnd, min, max, onSelect }) => {
  const weeks = useMemo(() => buildMonthMatrix(year, month), [year, month]);
  const disabled = (d: Date) => {
    if (min && isBefore(d, min)) return true;
    if (max && isAfter(d, max)) return true;
    return false;
  };
  const isInRange = (d: Date) => {
    if (selectedStart && selectedEnd) {
      const t = sod(d).getTime();
      return t >= sod(selectedStart).getTime() && t <= sod(selectedEnd).getTime();
    }
    return false;
  };
  const isSame = (a: Date | null, b: Date | null) => !!a && !!b && sod(a).getTime() === sod(b).getTime();
  return (
    <Grid container columns={7} spacing={0.5}>
      {weeks.map((w, wi) => (
        <React.Fragment key={wi}>
          {w.map((d, di) => (
            <Grid key={di} size={{ xs: 1 }}>
              {d ? (
                <Button
                  variant={isSame(d, selectedStart) || isSame(d, selectedEnd) ? 'contained' : isInRange(d) ? 'outlined' : 'text'}
                  color={isSame(d, selectedStart) || isSame(d, selectedEnd) ? 'primary' : 'inherit'}
                  disabled={disabled(d)}
                  onClick={() => onSelect(d)}
                  sx={{ minWidth: 0, width: '100%', height: 32 }}
                >
                  {d.getDate()}
                </Button>
              ) : (
                <Box sx={{ height: 32 }} />
              )}
            </Grid>
          ))}
        </React.Fragment>
      ))}
    </Grid>
  );
};

const MonthHeader: React.FC<{
  year: number;
  month: number;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onPrevYear: () => void;
  onNextYear: () => void;
}> = ({ year, month, onPrevMonth, onNextMonth, onPrevYear, onNextYear }) => {
  const monthLabel = useMemo(() => new Date(year, month, 1).toLocaleString(undefined, { month: 'long', year: 'numeric' }), [year, month]);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
      <Box>
        <IconButton size="small" onClick={onPrevYear} sx={{ mr: 0.5 }}><KeyboardDoubleArrowLeftIcon /></IconButton>
        <IconButton size="small" onClick={onPrevMonth}><ChevronLeftIcon /></IconButton>
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{monthLabel}</Typography>
      <Box>
        <IconButton size="small" onClick={onNextMonth}><ChevronRightIcon /></IconButton>
        <IconButton size="small" onClick={onNextYear} sx={{ ml: 0.5 }}><KeyboardDoubleArrowRightIcon /></IconButton>
      </Box>
    </Box>
  );
};

function WeekHeader() {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 0.5 }}>
      {days.map(d => <Typography key={d} sx={{ fontSize: '0.7rem', fontWeight: 600, width: 30, textAlign: 'center' }}>{d}</Typography>)}
    </Box>
  );
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 12-month grid for precision=month range selection (YearMonth compare). */
const YearMonthGrid: React.FC<{
  year: number;
  selectedStart: Date | null;
  selectedEnd: Date | null;
  min?: Date | null;
  max?: Date | null;
  onSelect: (d: Date) => void;
}> = ({ year, selectedStart, selectedEnd, min, max, onSelect }) => {
  const ym = (d: Date) => d.getFullYear() * 12 + d.getMonth();
  const disabled = (d: Date) => {
    if (min && ym(d) < ym(min)) return true;
    if (max && ym(d) > ym(max)) return true;
    return false;
  };
  const isInRange = (d: Date) => {
    if (selectedStart && selectedEnd) {
      const t = ym(d);
      return t >= ym(selectedStart) && t <= ym(selectedEnd);
    }
    return false;
  };
  const isSameMonth = (a: Date | null, b: Date | null) =>
    !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
  return (
    <Grid container columns={3} spacing={0.5}>
      {MONTH_LABELS.map((label, monthIndex) => {
        const d = new Date(year, monthIndex, 1);
        const selected = isSameMonth(d, selectedStart) || isSameMonth(d, selectedEnd);
        return (
          <Grid key={label} size={{ xs: 1 }}>
            <Button
              variant={selected ? 'contained' : isInRange(d) ? 'outlined' : 'text'}
              color={selected ? 'primary' : 'inherit'}
              disabled={disabled(d)}
              onClick={() => onSelect(d)}
              sx={{ minWidth: 0, width: '100%', height: 32 }}
            >
              {label}
            </Button>
          </Grid>
        );
      })}
    </Grid>
  );
};

export function RangeDatePicker({
  label,
  from,
  to,
  onChangeFrom,
  onChangeTo,
  minDate,
  maxDate,
  disabled,
  displayFrom = false,
  required,
  fullWidth,
  size = 'small',
  variant = 'outlined',
  compact = true,
  error,
  errorFrom,
  errorTo,
  precision = 'day',
}: RangeDatePickerProps) {
  const effectiveFullWidth = fullWidth ?? true;
  const fromLocked = Boolean(displayFrom);
  const isMonth = precision === 'month';
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const uid = useId();

  const parseBound = (v?: string | null) => parseDMY(v) || parseDDMMYYYY(v) || parseMMYYYY(v);
  const fromDate = useMemo(
    () => (isMonth ? (parseMMYYYY(from) || parseDMY(from)) : parseDMY(from)),
    [from, isMonth],
  );
  const toDate = useMemo(
    () => (isMonth ? (parseMMYYYY(to) || parseDMY(to)) : parseDMY(to)),
    [to, isMonth],
  );
  const min = useMemo(() => parseBound(minDate || null), [minDate]);
  const max = useMemo(() => parseBound(maxDate || null), [maxDate]);
  const formatValue = (d: Date) => (isMonth ? formatMMYYYY(d) : toDMY(d));

  // View base month based on from or today
  const [viewYear, setViewYear] = useState<number>(() => fromDate?.getFullYear() ?? new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState<number>(() => fromDate?.getMonth() ?? new Date().getMonth());

  // Temp selection flow: first select start, then end
  const [tempStart, setTempStart] = useState<Date | null>(fromDate);
  const [tempEnd, setTempEnd] = useState<Date | null>(toDate);
  const [step, setStep] = useState<'start' | 'end'>(() => (fromDate ? 'end' : 'start'));

  // Track raw input values for formatting
  const [fromInput, setFromInput] = useState<string>(() => {
    if (isMonth) {
      const p = parseMMYYYY(from) || parseDMY(from);
      return p ? formatMMYYYY(p) : (from || '');
    }
    return normalizeToDdMmYyyy(from) || from || '';
  });
  const [toInput, setToInput] = useState<string>(() => {
    if (isMonth) {
      const p = parseMMYYYY(to) || parseDMY(to);
      return p ? formatMMYYYY(p) : (to || '');
    }
    return normalizeToDdMmYyyy(to) || to || '';
  });
  const [formatErrorFrom, setFormatErrorFrom] = useState<string | undefined>();
  const [formatErrorTo, setFormatErrorTo] = useState<string | undefined>();
  
  // Use ref to track if we're in the middle of auto-filling to prevent loops
  const isAutoFillingRef = useRef(false);

  useEffect(() => setTempStart(fromDate), [fromDate]);
  useEffect(() => setTempEnd(toDate), [toDate]);
  useEffect(() => {
    if (!isAutoFillingRef.current) {
      if (isMonth) {
        const parsed = parseMMYYYY(from) || parseDMY(from);
        const normalized = parsed ? formatMMYYYY(parsed) : null;
        setFromInput(normalized || from || '');
        if (!from?.trim() || normalized) setFormatErrorFrom(undefined);
        if (from && normalized && normalized !== from) onChangeFrom(normalized);
      } else {
        const normalized = normalizeToDdMmYyyy(from);
        setFromInput(normalized || from || '');
        if (!from?.trim() || normalized) setFormatErrorFrom(undefined);
        if (from && normalized && normalized !== from) onChangeFrom(normalized);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, isMonth]);
  useEffect(() => {
    if (!isAutoFillingRef.current) {
      if (isMonth) {
        const parsed = parseMMYYYY(to) || parseDMY(to);
        const normalized = parsed ? formatMMYYYY(parsed) : null;
        setToInput(normalized || to || '');
        if (!to?.trim() || normalized) setFormatErrorTo(undefined);
        if (to && normalized && normalized !== to) onChangeTo(normalized);
      } else {
        const normalized = normalizeToDdMmYyyy(to);
        setToInput(normalized || to || '');
        if (!to?.trim() || normalized) setFormatErrorTo(undefined);
        if (to && normalized && normalized !== to) onChangeTo(normalized);
      }
    }
    isAutoFillingRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, isMonth]);

  const canGoPrev = useMemo(() => {
    if (!min) return true;
    const prevEnd = new Date(viewYear, viewMonth, 0);
    return !isBefore(prevEnd, min);
  }, [min, viewYear, viewMonth]);
  const canGoNext = useMemo(() => {
    if (!max) return true;
    const nextStart = new Date(viewYear, viewMonth + 1, 1);
    return !isAfter(nextStart, max);
  }, [max, viewYear, viewMonth]);
  const canGoPrevYear = useMemo(() => {
    if (!min) return true;
    const prevYearEnd = new Date(viewYear - 1, viewMonth + 1, 0);
    return !isBefore(prevYearEnd, min);
  }, [min, viewYear, viewMonth]);
  const canGoNextYear = useMemo(() => {
    if (!max) return true;
    const nextYearStart = new Date(viewYear + 1, viewMonth, 1);
    return !isAfter(nextYearStart, max);
  }, [max, viewYear, viewMonth]);

  // InputField-like styles
  const mergedSx = {
    position: 'relative',
    ...(compact ? { lineHeight: 1.2 } : {}),
    // Compact paddings for small size root
    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall, & .MuiFilledInput-root.MuiInputBase-sizeSmall, & .MuiInputBase-root.MuiInputBase-sizeSmall': {
      paddingTop: '3px',
      paddingBottom: '0.5px',
      paddingLeft: '3px',
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
    // Input inner paddings and right space for icon
    '& .MuiInputBase-input': {
      fontSize: (theme.typography as any).body2?.fontSize || undefined,
      lineHeight: 1.2,
      paddingTop: '3px',
      paddingBottom: '3px',
      paddingRight: '3px', // leave room for overlaid icon
    },
    // Position the end adornment icon 2px from the right across variants
    '& .MuiInputAdornment-positionEnd': {
      position: 'absolute',
      right: 2,
      top: '50%',
      transform: 'translateY(-50%)',
      margin: 0,
      height: 'auto',
      maxHeight: '100%',
      pointerEvents: 'auto',
    },
    // Hover on icon should follow variant border color
    '& .MuiInputAdornment-positionEnd .MuiIconButton-root:hover svg': {
      color: theme.palette.primary.main,
    },
  } as any;

  const openPicker = () => {
    if (disabled) return;
    // sync with current values
    if (fromDate) {
      setViewYear(fromDate.getFullYear());
      setViewMonth(fromDate.getMonth());
    }
    if (fromLocked && fromDate) {
      // From is display-only: jump to selecting To
      setTempStart(fromDate);
      setTempEnd(toDate);
      setStep('end');
    } else {
      // Always start selection from the start date like Angular Material range picker
      setStep('start');
    }
    setOpen(true);
  };
  const closePicker = () => setOpen(false);

  const handleSelect = (d: Date) => {
    // Renewal From-locked path only — keep default range behavior untouched below.
    if (fromLocked) {
      if (step === 'start') {
        setTempStart(fromDate || d);
        setTempEnd(null);
        setStep('end');
        return;
      }
      let s = fromDate;
      let e = d;
      if (s && isAfter(s, e)) {
        e = s;
      }
      setTempStart(s || d);
      setTempEnd(e);
      onChangeTo(formatValue(e));
      setOpen(false);
      return;
    }

    if (step === 'start') {
      // Start a new range selection: set start and call onChangeFrom immediately
      setTempStart(d);
      setTempEnd(null);
      onChangeFrom(formatValue(d));
      // Don't clear the end date to avoid state conflicts - let the parent manage it
      setStep('end');
    } else {
      let s = tempStart;
      let e = d;
      if (s && isAfter(s, e)) {
        // swap
        const t = s; s = e; e = t;
      }
      setTempStart(s || d);
      setTempEnd(e);
      onChangeFrom(s ? formatValue(s) : formatValue(d));
      onChangeTo(formatValue(e));
      setOpen(false);
    }
  };

  const handlePrevMonth = () => {
    if (!canGoPrev) return;
    const m = viewMonth - 1;
    if (m < 0) { setViewYear(viewYear - 1); setViewMonth(11); } else { setViewMonth(m); }
  };
  const handleNextMonth = () => {
    if (!canGoNext) return;
    const m = viewMonth + 1;
    if (m > 11) { setViewYear(viewYear + 1); setViewMonth(0); } else { setViewMonth(m); }
  };
  const handlePrevYear = () => { if (canGoPrevYear) setViewYear(y => y - 1); };
  const handleNextYear = () => { if (canGoNextYear) setViewYear(y => y + 1); };

  const nextMonthYear = useMemo(() => {
    const m = viewMonth + 1;
    return m > 11 ? { y: viewYear + 1, m: 0 } : { y: viewYear, m };
  }, [viewMonth, viewYear]);

  const fromInputRef = useRef<HTMLInputElement>(null);
  const toInputRef = useRef<HTMLInputElement>(null);

  // Only show error when both from and to are required but both are missing
  // Or when external error prop is explicitly set
  const showError = (required && !from && !to) || (error === true) || (errorFrom && errorTo)
    || !!formatErrorFrom || !!formatErrorTo;
  const formatHelper = formatErrorFrom || formatErrorTo;

  const clampDate = (d: Date) => {
    if (isMonth) {
      const ym = (x: Date) => x.getFullYear() * 12 + x.getMonth();
      if (min && ym(d) < ym(min)) return new Date(min.getFullYear(), min.getMonth(), 1);
      if (max && ym(d) > ym(max)) return new Date(max.getFullYear(), max.getMonth(), 1);
      return d;
    }
    if (min && isBefore(d, min)) return min;
    if (max && isAfter(d, max)) return max;
    return d;
  };

  const commitFromBlur = (raw: string) => {
    const trimmed = (raw || '').trim();
    if (!trimmed) {
      setFormatErrorFrom(undefined);
      setFromInput('');
      onChangeFrom(null);
      return;
    }
    if (isMonth) {
      const parsed = parseMMYYYY(formatMonthYearInput(trimmed));
      if (!parsed) {
        setFormatErrorFrom(MONTH_YEAR_FORMAT_HINT);
        return;
      }
      setFormatErrorFrom(undefined);
      const corrected = clampDate(parsed);
      const formatted = formatMMYYYY(corrected);
      setFromInput(formatted);
      onChangeFrom(formatted);
      return;
    }
    const normalized = normalizeToDdMmYyyy(trimmed);
    if (!normalized) {
      setFormatErrorFrom(DATE_FORMAT_HINT);
      return;
    }
    setFormatErrorFrom(undefined);
    let correctedDate = parseDMY(normalized)!;
    correctedDate = clampDate(correctedDate);
    const formatted = toDMY(correctedDate);
    setFromInput(formatted);
    onChangeFrom(formatted);
    // Auto-fill 'to' field if 'from' is 01/04/yyyy
    if (correctedDate.getDate() === 1 && correctedDate.getMonth() === 3) {
      const nextYear = correctedDate.getFullYear() + 1;
      const autoToDate = new Date(nextYear, 2, 31);
      const autoToFormatted = toDMY(autoToDate);
      if (!to || to !== autoToFormatted) {
        isAutoFillingRef.current = true;
        setFormatErrorTo(undefined);
        setToInput(autoToFormatted);
        onChangeTo(autoToFormatted);
      }
    }
  };

  const commitToBlur = (raw: string) => {
    const trimmed = (raw || '').trim();
    if (!trimmed) {
      setFormatErrorTo(undefined);
      setToInput('');
      onChangeTo(null);
      return;
    }
    if (isMonth) {
      const parsed = parseMMYYYY(formatMonthYearInput(trimmed));
      if (!parsed) {
        setFormatErrorTo(MONTH_YEAR_FORMAT_HINT);
        return;
      }
      setFormatErrorTo(undefined);
      const corrected = clampDate(parsed);
      const formatted = formatMMYYYY(corrected);
      setToInput(formatted);
      onChangeTo(formatted);
      return;
    }
    const normalized = normalizeToDdMmYyyy(trimmed);
    if (!normalized) {
      setFormatErrorTo(DATE_FORMAT_HINT);
      return;
    }
    setFormatErrorTo(undefined);
    let correctedDate = parseDMY(normalized)!;
    correctedDate = clampDate(correctedDate);
    const formatted = toDMY(correctedDate);
    setToInput(formatted);
    onChangeTo(formatted);
  };

  // Custom styled range input with two fields and separator
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: effectiveFullWidth ? '100%' : undefined, flexGrow: effectiveFullWidth ? 1 : 0 }} ref={anchorRef}>
      <TextField
        label={label}
        variant={variant}
        size={size}
        fullWidth={effectiveFullWidth}
        disabled={disabled}
        required={required}
        error={showError}
        sx={{ ...mergedSx }}
        InputProps={{
          startAdornment: (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              width: '100%',
              gap: 0.5,
            }}>
              <Box
                component="input"
                ref={fromInputRef}
                value={fromInput}
                onChange={(e: any) => {
                  const newValue = e.target.value;
                  setFormatErrorFrom(undefined);
                  // Allow deletion - if new value is shorter, use it directly
                  if (newValue.length < fromInput.length) {
                    setFromInput(newValue);
                    if (!newValue.trim()) onChangeFrom(null);
                  } else if (isMonth) {
                    const formatted = formatMonthYearInput(newValue);
                    setFromInput(formatted);
                    const complete = parseMMYYYY(formatted);
                    if (complete) onChangeFrom(formatMMYYYY(complete));
                  } else {
                    const formatted = formatDateInput(newValue);
                    setFromInput(formatted);
                    const complete = parseDMY(formatted);
                    onChangeFrom(complete ? formatted : (formatted || null));
                  }
                }}
                onBlur={(e: any) => commitFromBlur(e.target.value || '')}
                placeholder={isMonth ? 'MM/YYYY' : 'dd/MM/yyyy'}
                disabled={disabled || fromLocked}
                sx={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  textAlign: 'center',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  padding: 0,
                  minWidth: 0,
                  '&::placeholder': {
                    color: 'text.disabled',
                    opacity: 0.6,
                  },
                  '&:disabled': {
                    color: 'text.disabled',
                  },
                }}
              />
              <Typography sx={{ fontWeight: 'bold', color: 'text.secondary', px: 0.5 }}>~</Typography>
              <Box
                component="input"
                ref={toInputRef}
                value={toInput}
                onChange={(e: any) => {
                  const newValue = e.target.value;
                  setFormatErrorTo(undefined);
                  // Allow deletion - if new value is shorter, use it directly
                  if (newValue.length < toInput.length) {
                    setToInput(newValue);
                    if (!newValue.trim()) onChangeTo(null);
                  } else if (isMonth) {
                    const formatted = formatMonthYearInput(newValue);
                    setToInput(formatted);
                    const complete = parseMMYYYY(formatted);
                    if (complete) onChangeTo(formatMMYYYY(complete));
                  } else {
                    const formatted = formatDateInput(newValue);
                    setToInput(formatted);
                    const complete = parseDMY(formatted);
                    onChangeTo(complete ? formatted : (formatted || null));
                  }
                }}
                onBlur={(e: any) => commitToBlur(e.target.value || '')}
                placeholder={isMonth ? 'MM/YYYY' : 'dd/MM/yyyy'}
                disabled={disabled}
                sx={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  textAlign: 'center',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  padding: 0,
                  minWidth: 0,
                  '&::placeholder': {
                    color: 'text.disabled',
                    opacity: 0.6,
                  },
                  '&:disabled': {
                    color: 'text.disabled',
                  },
                }}
              />
            </Box>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={openPicker} disabled={disabled} sx={{ p: 0.5 }}>
                <CalendarMonthIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        inputProps={{
          style: { display: 'none' }, // Hide the default input
        }}
      />
      {formatHelper && (
        <FormHelperText error sx={{ ml: 1.5 }}>
          {formatHelper}
        </FormHelperText>
      )}

      <Popover
        open={open}
        onClose={closePicker}
        anchorEl={anchorRef.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disablePortal
        container={anchorRef.current ? (anchorRef.current.closest('.MuiDialog-root') as HTMLElement) || (anchorRef.current.parentElement as HTMLElement) : undefined}
      >
        <Box sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ width: 240 }}>
              {isMonth ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <IconButton size="small" onClick={handlePrevYear} disabled={!canGoPrevYear}>
                      <KeyboardDoubleArrowLeftIcon />
                    </IconButton>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{viewYear}</Typography>
                    <IconButton size="small" onClick={handleNextYear} disabled={!canGoNextYear}>
                      <KeyboardDoubleArrowRightIcon />
                    </IconButton>
                  </Box>
                  <YearMonthGrid
                    year={viewYear}
                    selectedStart={tempStart}
                    selectedEnd={tempEnd}
                    min={min}
                    max={max}
                    onSelect={handleSelect}
                  />
                </>
              ) : (
                <>
                  <MonthHeader year={viewYear} month={viewMonth} onPrevMonth={handlePrevMonth} onNextMonth={handleNextMonth} onPrevYear={handlePrevYear} onNextYear={handleNextYear} />
                  <WeekHeader />
                  <MonthGrid
                    year={viewYear}
                    month={viewMonth}
                    selectedStart={tempStart}
                    selectedEnd={tempEnd}
                    min={min}
                    max={max}
                    onSelect={handleSelect}
                  />
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}
